import React, { FC, FormEvent, useState } from "react";

import { toast } from "react-toastify";

import {
  useApisManagersEmployeesEmployeeMemoReviewsCompletedsUpdate,
  useApisManagersEmployeesEmployeeMemoReviewsDestroy,
  useApisManagersEmployeesEmployeeMemoReviewsUpdate,
  useBoolean,
  useEditorState,
} from "~/hooks";

import {
  EmployeeAndBusinessSectionMemoReviewForm,
  PostContentEditorWithImages,
  PostHeader,
} from "~/components/organisms";

import {
  EmployeeMemoReviewType,
  PostDetailMenuItemType,
  PreviewWithFileType,
} from "~/domains";

import { PostDetailWrapper } from "~/ui";
import { CompletedMarkMessage } from "~/ui";

type PropsType = {
  employeeMemoReview: EmployeeMemoReviewType;
  updateIndexPost: (newItem: EmployeeMemoReviewType) => void;
  deleteIndexPost: () => void;
  className?: string;
};

export const EmployeeMemoReviewDetailWithEditForm: FC<PropsType> = ({
  employeeMemoReview,
  updateIndexPost,
  deleteIndexPost,
  className,
}: PropsType) => {
  const { isChecked, setTrue, setFalse } = useBoolean(false);
  const [files, setFiles] = useState<PreviewWithFileType[]>([]);
  const [
    {
      jsonContent: editorStateJsonContent,
      value: editorState,
      onChange: onChangeEditorState,
    },
  ] = useEditorState(employeeMemoReview.content);

  const { mutate: updateRequest, isLoading: isUpdateSubmitting } =
    useApisManagersEmployeesEmployeeMemoReviewsUpdate();
  const { mutate: completeRequest, isLoading: isCompleteSubmitting } =
    useApisManagersEmployeesEmployeeMemoReviewsCompletedsUpdate();
  const { mutate: deleteRequest } =
    useApisManagersEmployeesEmployeeMemoReviewsDestroy();

  const handleUpdateRequest = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    updateRequest(
      {
        employeeId: employeeMemoReview.employee.id,
        id: employeeMemoReview.id,
        body: {
          content: editorStateJsonContent,
          ...(files.length > 0 ? { attachFiles: files } : {}),
        },
      },
      {
        onSuccess: (data) => {
          toast(data.message);
          updateIndexPost(data.employeeMemoReview);
          setFalse();
        },
      },
    );
  };

  const handleCompleteRequest = () => {
    const isConfirm = confirm("本当に更新にしますか？");
    if (!isConfirm) return;

    completeRequest(
      {
        employeeId: employeeMemoReview.employee.id,
        employeeMemoReviewId: employeeMemoReview.id,
      },
      {
        onSuccess: (data) => {
          toast(data.message);
          updateIndexPost(data.employeeMemoReview);
          setFalse();
        },
      },
    );
  };

  const handleDeleteRequest = () => {
    const isConfirm = confirm("本当に削除しますか？");
    if (!isConfirm) return;

    deleteRequest(
      {
        employeeId: employeeMemoReview.employee.id,
        id: employeeMemoReview.id,
      },
      {
        onSuccess: (data) => {
          toast(data.message);
          deleteIndexPost();
        },
      },
    );
  };

  const cancelItems = [
    {
      text: "編集をキャンセル",
      onClick: () => setFalse(),
    },
  ];

  const menuItems: PostDetailMenuItemType[] = [
    {
      icon: "ioCreateOutline",
      text: "編集する",
      onClick: () => setTrue(),
    },
    {
      icon: employeeMemoReview.completedAt
        ? "ioRemoveCircleOutline"
        : "ioCheckmarkCircleOutline",
      text: employeeMemoReview.completedAt ? "未完了にする" : "完了済みにする",
      onClick: () => handleCompleteRequest(),
    },
    {
      icon: "ioTrashOutline",
      text: "削除する",
      onClick: () => handleDeleteRequest(),
    },
  ];

  return (
    <div className={`${className}`}>
      {isChecked ? (
        <PostDetailWrapper skipHoverStyle>
          <PostHeader
            miniText={employeeMemoReview.createdAt}
            postedEmployee={employeeMemoReview.reviewer}
            menuItems={cancelItems}
            underLabel={employeeMemoReview.reviewer.name}
          />
          <EmployeeAndBusinessSectionMemoReviewForm
            editorState={editorState}
            onChangeEditorState={onChangeEditorState}
            handleSubmit={handleUpdateRequest}
            isSubmitting={isUpdateSubmitting || isCompleteSubmitting}
            files={files}
            onChangeFiles={setFiles}
            className="mt-1 pl-16"
          />
        </PostDetailWrapper>
      ) : (
        <PostDetailWrapper>
          {employeeMemoReview.completedAt && (
            <CompletedMarkMessage message="完了済み" />
          )}
          <PostHeader
            miniText={employeeMemoReview.createdAt}
            postedEmployee={employeeMemoReview.reviewer}
            menuItems={menuItems}
            underLabel={employeeMemoReview.reviewer.name}
            className={employeeMemoReview.completedAt ? "mt-2" : ""}
          />
          <PostContentEditorWithImages
            content={employeeMemoReview.content}
            files={employeeMemoReview.attachFiles}
            className="mt-1 pl-16"
          />
        </PostDetailWrapper>
      )}
    </div>
  );
};
